// src/features/home/home.slice.ts
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { get } from "../../../utils/apiUtils";
import dayjs from "dayjs";

/* ----------------------------- Types ----------------------------- */
export type HomeDocRow = {
  key: string;
  file_key_id?: string;
  username: string;
  documentName: string;
  highlightLength: number;
  dateCreated: string;
  modifiedBy: string;
};

type SummaryState = {
  total_members: number;
  total_codes: number;
  total_documents: number;
  total_highlights: number;
  loading: boolean;
};

type DocsState = {
  items: HomeDocRow[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
};

export type HomeState = {
  projectId: string | null;
  summary: SummaryState;
  docs: DocsState;
};

/* --------------------------- Initial ----------------------------- */
const initialState: HomeState = {
  projectId: null,
  summary: {
    total_members: 0,
    total_codes: 0,
    total_documents: 0,
    total_highlights: 0,
    loading: false,
  },
  docs: {
    items: [],
    total: 0,
    page: 1,
    pageSize: 10,
    loading: false,
  },
};

/* ---------------------------- Thunks ----------------------------- */
// ⚠️ tani pranojnë projectId opsional që mund ta dërgojmë nga Media
export const fetchHomeSummary = createAsyncThunk(
  "home/fetchSummary",
  async (arg: { projectId?: string } | void, { getState }) => {
    const s = getState() as { home: HomeState };
    const projectId = (arg as any)?.projectId ?? s.home.projectId;
    if (!projectId) throw new Error("Missing project id");

    const res = await get<any>(`/projects/${projectId}/summary`);
    if (res.type !== "success") throw new Error(res?.data?.message || "Failed");
    return res.data as Omit<SummaryState, "loading">;
  }
);

export const fetchHomeDocuments = createAsyncThunk(
  "home/fetchDocuments",
  async (arg: { projectId?: string } | void, { getState }) => {
    const s = getState() as { home: HomeState };
    const projectId = (arg as any)?.projectId ?? s.home.projectId;
    const { page, pageSize } = s.home.docs;
    if (!projectId) throw new Error("Missing project id");

    const skip = (page - 1) * pageSize;
    const res = await get<any>(`/documents`, {
      params: { project_id: projectId, skip, limit: pageSize },
      headers: { Accept: "application/json" },
    });
    if (res.type !== "success") throw new Error(res?.data?.message || "Failed");

    const itemsRaw: any[] = res.data?.items ?? [];
    itemsRaw.sort(
      (a, b) =>
        new Date(b.created_at || b.uploaded_at || 0).getTime() -
        new Date(a.created_at || a.uploaded_at || 0).getTime()
    );

    const items: HomeDocRow[] = itemsRaw.map((doc: any) => ({
      key: doc.document_id,
      file_key_id: doc.file_key_id,
      username: doc.uploaded_by || "Unknown",
      documentName: doc.document_name,
      highlightLength: doc.highlight_length_count || 0,
      dateCreated: dayjs(doc.uploaded_at || doc.created_at).format(
        "MM/DD/YYYY"
      ),
      modifiedBy: doc.modified_by || "Unknown",
    }));

    return { items, total: res.data?.total ?? items.length };
  }
);

/* ---------------------------- Slice ------------------------------ */
const homeSlice = createSlice({
  name: "home",
  initialState,
  reducers: {
    setHomeProjectId(state, action: PayloadAction<string | null>) {
      state.projectId = action.payload;
      state.docs.items = [];
      state.docs.total = 0;
      state.docs.page = 1;
      state.summary.total_codes = 0;
      state.summary.total_documents = 0;
      state.summary.total_highlights = 0;
      state.summary.total_members = 0;
    },
    setDocsPage(state, action: PayloadAction<number>) {
      state.docs.page = action.payload;
    },
    setDocsPageSize(state, action: PayloadAction<number>) {
      state.docs.pageSize = action.payload;
      state.docs.page = 1;
    },
  },
  extraReducers: (b) => {
    b.addCase(fetchHomeSummary.pending, (s) => {
      s.summary.loading = true;
    });
    b.addCase(fetchHomeSummary.fulfilled, (s, a) => {
      s.summary.loading = false;
      const d = a.payload;
      s.summary.total_members = d.total_members ?? 0;
      s.summary.total_codes = d.total_codes ?? 0;
      s.summary.total_documents = d.total_documents ?? 0;
      s.summary.total_highlights = d.total_highlights ?? 0;
    });
    b.addCase(fetchHomeSummary.rejected, (s) => {
      s.summary.loading = false;
    });

    b.addCase(fetchHomeDocuments.pending, (s) => {
      s.docs.loading = true;
    });
    b.addCase(fetchHomeDocuments.fulfilled, (s, a) => {
      s.docs.loading = false;
      s.docs.items = a.payload.items;
      s.docs.total = a.payload.total;
    });
    b.addCase(fetchHomeDocuments.rejected, (s) => {
      s.docs.loading = false;
    });
  },
});

export const { setHomeProjectId, setDocsPage, setDocsPageSize } =
  homeSlice.actions;
export default homeSlice.reducer;
